/**
 * On-hydrate migration helpers for ADR-0069 — fold legacy single-
 * `Money` cost storage (ADR-0048) and the `Trip.costAttribution`
 * sidecar (ADR-0059) into the new per-line-item `costs` shape.
 *
 * Idempotent: trips already on the new shape pass through
 * untouched. Runs from the upsertTrip path (hydrate / duplicate /
 * import) so readers downstream always see `costs[]`.
 *
 * The TS types for `Trip` no longer carry the legacy fields, so the
 * input is read through a wider `LegacyTrip` shape via unchecked
 * casts. The wire / persisted JSON may still carry the old fields
 * for older clients — we strip them here on read.
 */

import type {
  Booking,
  CostLineItem,
  CustomEntry,
  Day,
  Journey,
  Place,
  Stay,
  Trip,
  TripParticipant,
} from '@my-fullstack-app/itinerary-types';
import { generateId } from './id';

/** Wire-shape echoes of the pre-ADR-0069 / pre-ADR-0059 fields.
 *  Used only at the migration boundary. */
interface LegacyMoney {
  amount?: unknown;
  currency?: unknown;
}

interface LegacyAttribution {
  paidBy?: unknown;
  splitWith?: unknown;
  categoryId?: unknown;
}

type LegacyAttributionMap = Record<string, LegacyAttribution | undefined>;

type LegacyPlace = Place & { cost?: LegacyMoney | undefined };
type LegacyCustom = CustomEntry & { cost?: LegacyMoney | undefined };
type LegacyBooking = Booking & { pricePaid?: LegacyMoney | undefined };
type LegacyJourney = Omit<Journey, 'booking'> & { booking?: LegacyBooking | undefined };
type LegacyStay = Omit<Stay, 'booking'> & { booking?: LegacyBooking | undefined };
type LegacyDay = Omit<Day, 'places' | 'customs'> & {
  places: LegacyPlace[];
  customs?: LegacyCustom[] | undefined;
};
type LegacyTrip = Omit<Trip, 'days' | 'journeys' | 'stays'> & {
  days: LegacyDay[];
  journeys?: LegacyJourney[] | undefined;
  stays?: LegacyStay[] | undefined;
  costAttribution?: LegacyAttributionMap | undefined;
};

/** Read a single Money-shaped object into a CostLineItem. Drops
 *  the record (returns undefined) when amount isn't a positive
 *  finite number or currency isn't a non-empty string — matches
 *  the read-time validation `getEntityCost` used to do. */
function legacyMoneyToLineItem(
  money: LegacyMoney | undefined,
  attribution: LegacyAttribution | undefined,
): CostLineItem | undefined {
  if (!money) return undefined;
  const amount = money.amount;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return undefined;
  const currency = typeof money.currency === 'string' ? money.currency.trim().toUpperCase() : '';
  if (!currency) return undefined;
  const item: CostLineItem = {
    id: generateId(),
    amount,
    currency,
  };
  if (attribution) {
    const paidBy = typeof attribution.paidBy === 'string' && attribution.paidBy ? attribution.paidBy : undefined;
    if (paidBy) item.paidBy = paidBy;
    if (Array.isArray(attribution.splitWith)) {
      item.splitWith = attribution.splitWith.filter((x): x is string => typeof x === 'string' && !!x);
    }
    const categoryId = typeof attribution.categoryId === 'string' && attribution.categoryId ? attribution.categoryId : undefined;
    if (categoryId) item.categoryId = categoryId;
  }
  return item;
}

/** Strip legacy `cost` / `pricePaid` and produce a clean entity. */
function migratePlace(place: LegacyPlace, attribution: LegacyAttribution | undefined): Place {
  // Already on the new shape — no legacy field present.
  if (place.cost === undefined) return place;
  const { cost, ...rest } = place;
  const item = legacyMoneyToLineItem(cost, attribution);
  return item ? { ...rest, costs: [...(rest.costs ?? []), item] } : rest;
}

function migrateCustom(custom: LegacyCustom, attribution: LegacyAttribution | undefined): CustomEntry {
  if (custom.cost === undefined) return custom;
  const { cost, ...rest } = custom;
  const item = legacyMoneyToLineItem(cost, attribution);
  return item ? { ...rest, costs: [...(rest.costs ?? []), item] } : rest;
}

function migrateBooking(
  booking: LegacyBooking | undefined,
  attribution: LegacyAttribution | undefined,
): Booking | undefined {
  if (!booking) return undefined;
  if (booking.pricePaid === undefined) return booking;
  const { pricePaid, ...rest } = booking;
  const item = legacyMoneyToLineItem(pricePaid, attribution);
  return item ? { ...rest, costs: [...(rest.costs ?? []), item] } : rest;
}

/**
 * Strip the legacy `friendlyName` (nickname) and `colorIndex`
 * fields from every participant on a trip. Both fields were
 * removed in the ADR-0057 inline-edit amendment; persisted blobs
 * and server payloads from older clients may still carry them,
 * so this runs on every upsertTrip to keep in-memory trips on
 * the new shape.
 *
 * Idempotent: returns the same trip / array refs when no legacy
 * field is present.
 */
export function migrateTripParticipants(trip: Trip): Trip {
  const participants = trip.participants;
  if (!participants?.length) return trip;
  let changed = false;
  const next = participants.map((p) => {
    const legacy = p as TripParticipant & { friendlyName?: unknown; colorIndex?: unknown };
    if (legacy.friendlyName === undefined && legacy.colorIndex === undefined) return p;
    changed = true;
    const { friendlyName: _f, colorIndex: _c, ...rest } = legacy;
    return rest;
  });
  if (!changed) return trip;
  return { ...trip, participants: next };
}

/**
 * Walk a trip and produce a new one with all legacy cost fields
 * folded into `costs[]` arrays, with attribution applied to the
 * matching folded item. Drops `Trip.costAttribution` from the
 * returned trip — the wire-shape strip happens on next flush.
 *
 * Returns the same trip reference when nothing needed migrating
 * so React-Query / Zustand consumers can short-circuit on
 * identity.
 */
export function migrateTripCosts(trip: Trip): Trip {
  const legacy = trip as LegacyTrip;
  const attributionMap = legacy.costAttribution ?? {};
  const hasAttribution = Object.keys(attributionMap).length > 0;
  let changed = false;

  const attr = (kind: 'place' | 'custom' | 'journey' | 'stay', id: string): LegacyAttribution | undefined => {
    return attributionMap[`${kind}:${id}`];
  };

  const days = legacy.days.map((day) => {
    let dayChanged = false;
    const places = day.places.map((place) => {
      if (place.cost === undefined) return place;
      dayChanged = true;
      return migratePlace(place, attr('place', place.id));
    });
    const customs = day.customs?.map((custom) => {
      if (custom.cost === undefined) return custom;
      dayChanged = true;
      return migrateCustom(custom, attr('custom', custom.id));
    });
    if (!dayChanged) return day;
    changed = true;
    return {
      ...day,
      places,
      ...(customs ? { customs } : {}),
    };
  });

  const journeys = legacy.journeys?.map((journey) => {
    if (journey.booking?.pricePaid === undefined) return journey;
    changed = true;
    const booking = migrateBooking(journey.booking, attr('journey', journey.id));
    return { ...journey, ...(booking ? { booking } : { booking: undefined }) };
  });

  const stays = legacy.stays?.map((stay) => {
    if (stay.booking?.pricePaid === undefined) return stay;
    changed = true;
    const booking = migrateBooking(stay.booking, attr('stay', stay.id));
    return { ...stay, ...(booking ? { booking } : { booking: undefined }) };
  });

  if (!changed && !hasAttribution) return trip;

  const out: Trip = {
    ...trip,
    days,
    ...(journeys ? { journeys } : {}),
    ...(stays ? { stays } : {}),
  };
  // Drop the legacy sidecar field from the in-memory model. Any
  // attribution that didn't land on a folded item is dropped
  // (orphan attribution — the entity it referenced is gone or
  // never had a cost in the first place). The sync-manager flush
  // path will stop writing `costAttribution` once it's not in the
  // TripField list.
  const cleaned = { ...out } as Trip & { costAttribution?: unknown };
  delete cleaned.costAttribution;
  return cleaned;
}

/**
 * Attribute every cost line item with no `paidBy` to a single
 * participant. Used when a trip transitions from 0 → 1 participants:
 * the natural payer for everything entered before any participant
 * existed is whoever just joined. Also runs on `upsertTrip` for trips
 * that arrive from the server with exactly one participant — solo
 * trips hydrated cross-device land with their orphans attributed.
 *
 * Items that already have `paidBy` set are left untouched — this
 * never overrides an explicit attribution.
 *
 * Preserves referential identity per top-level collection (days /
 * journeys / stays): callers can `after.days !== before.days` to
 * tell which slices were actually touched, then mark only those
 * fields dirty. Avoids a fan-out that would clobber a co-editor's
 * concurrent changes via mergeServerTrip's local-wins semantics.
 *
 * Returns the same trip reference when nothing changed.
 */
export function attributeOrphanCosts(trip: Trip, paidBy: string): Trip {
  const fillItems = (items: CostLineItem[] | undefined): CostLineItem[] | undefined => {
    if (!items?.length) return items;
    let localChanged = false;
    const next = items.map((item) => {
      if (item.paidBy !== undefined) return item;
      localChanged = true;
      return { ...item, paidBy };
    });
    return localChanged ? next : items;
  };

  const newDays = mapPreservingIdentity(trip.days, (day) => {
    const places = mapPreservingIdentity(day.places, (p) => {
      const costs = fillItems(p.costs);
      return costs === p.costs ? p : { ...p, costs };
    });
    const customs = day.customs
      ? mapPreservingIdentity(day.customs, (c) => {
          const costs = fillItems(c.costs);
          return costs === c.costs ? c : { ...c, costs };
        })
      : day.customs;
    if (places === day.places && customs === day.customs) return day;
    return { ...day, places, ...(customs ? { customs } : {}) };
  });
  const newJourneys = trip.journeys
    ? mapPreservingIdentity(trip.journeys, (j) => {
        if (!j.booking) return j;
        const costs = fillItems(j.booking.costs);
        if (costs === j.booking.costs) return j;
        return { ...j, booking: { ...j.booking, costs } };
      })
    : trip.journeys;
  const newStays = trip.stays
    ? mapPreservingIdentity(trip.stays, (s) => {
        if (!s.booking) return s;
        const costs = fillItems(s.booking.costs);
        if (costs === s.booking.costs) return s;
        return { ...s, booking: { ...s.booking, costs } };
      })
    : trip.stays;

  if (
    newDays === trip.days &&
    newJourneys === trip.journeys &&
    newStays === trip.stays
  ) {
    return trip;
  }
  return {
    ...trip,
    days: newDays,
    ...(newJourneys ? { journeys: newJourneys } : {}),
    ...(newStays ? { stays: newStays } : {}),
  };
}

/** Map an array, returning the SAME reference when no element changed
 *  (per identity equality). Lets callers compare top-level refs to
 *  decide whether to mark a sync field dirty. */
function mapPreservingIdentity<T>(arr: T[], fn: (item: T) => T): T[] {
  let changed = false;
  const next = arr.map((item) => {
    const out = fn(item);
    if (out !== item) changed = true;
    return out;
  });
  return changed ? next : arr;
}

/**
 * Read-time prune for stale `paidBy` / `splitWith` / `categoryId`
 * refs across every line item on a trip. Replaces ADR-0059's
 * `pruneCostAttribution`: same cleanup contract, applied per-item
 * inline rather than to the sidecar map.
 *
 *  - `paidBy` not matching any current participant → cleared
 *    (item kept, attribution lost — same shape as never-attributed).
 *  - `splitWith[]` entries not matching any current participant →
 *    filtered out (item kept, list narrowed).
 *  - `categoryId` not matching any current category → cleared.
 *
 * Idempotent. Returns the same trip reference when nothing
 * changed.
 */
export function pruneTripCosts(trip: Trip): Trip {
  const participantIds = new Set((trip.participants ?? []).map((p) => p.id));
  const categoryIds = new Set((trip.categories ?? []).map((c) => c.id));
  let changed = false;

  const pruneItems = (items: CostLineItem[] | undefined): CostLineItem[] | undefined => {
    if (!items?.length) return items;
    let localChanged = false;
    const next = items.map((item) => {
      let nextItem = item;
      if (item.paidBy !== undefined && !participantIds.has(item.paidBy)) {
        const { paidBy: _drop, ...rest } = nextItem;
        nextItem = rest;
        localChanged = true;
      }
      if (item.splitWith !== undefined) {
        const narrowed = item.splitWith.filter((id) => participantIds.has(id));
        if (narrowed.length !== item.splitWith.length) {
          nextItem = { ...nextItem, splitWith: narrowed };
          localChanged = true;
        }
      }
      if (item.categoryId !== undefined && !categoryIds.has(item.categoryId)) {
        const { categoryId: _drop, ...rest } = nextItem;
        nextItem = rest;
        localChanged = true;
      }
      return nextItem;
    });
    if (!localChanged) return items;
    changed = true;
    return next;
  };

  const days = trip.days.map((day) => {
    const places = day.places.map((p) => {
      const costs = pruneItems(p.costs);
      return costs === p.costs ? p : { ...p, ...(costs ? { costs } : { costs: undefined }) };
    });
    const customs = day.customs?.map((c) => {
      const costs = pruneItems(c.costs);
      return costs === c.costs ? c : { ...c, ...(costs ? { costs } : { costs: undefined }) };
    });
    return { ...day, places, ...(customs ? { customs } : {}) };
  });
  const journeys = trip.journeys?.map((j) => {
    if (!j.booking) return j;
    const costs = pruneItems(j.booking.costs);
    if (costs === j.booking.costs) return j;
    return { ...j, booking: { ...j.booking, ...(costs ? { costs } : { costs: undefined }) } };
  });
  const stays = trip.stays?.map((s) => {
    if (!s.booking) return s;
    const costs = pruneItems(s.booking.costs);
    if (costs === s.booking.costs) return s;
    return { ...s, booking: { ...s.booking, ...(costs ? { costs } : { costs: undefined }) } };
  });

  if (!changed) return trip;
  return {
    ...trip,
    days,
    ...(journeys ? { journeys } : {}),
    ...(stays ? { stays } : {}),
  };
}
